import { lazy, Suspense, useState, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { DomainConfigProvider } from "./contexts/DomainConfigContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import Index from "./pages/Index";
import RouteLoading from "@/components/RouteLoading";

// Lazy-load toasters to keep them out of the main bundle
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Defer toast rendering until browser is idle to avoid blocking TTI
const DeferredToasters = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(() => setReady(true), { timeout: 3000 });
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(() => setReady(true), 2000);
      return () => clearTimeout(id);
    }
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <Toaster />
      <Sonner />
    </Suspense>
  );
};

// Lazy load non-critical routes
const AboutUs = lazy(() => import("./pages/AboutUs"));

const FAQs = lazy(() => import("./pages/FAQs"));
const Services = lazy(() => import("./pages/Services"));
const Blogs = lazy(() => import("./pages/Blogs"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const ContactUs = lazy(() => import("./pages/ContactUs"));

const SelectParking = lazy(() => import("./pages/SelectParking"));
const BookingProfile = lazy(() => import("./pages/BookingProfile"));
const BookingConfirm = lazy(() => import("./pages/BookingConfirm"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const BookingCancelled = lazy(() => import("./pages/BookingCancelled"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const TestimonialsPage = lazy(() => import("./pages/TestimonialsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <DomainConfigProvider>
      <LanguageProvider>
        <TooltipProvider>
          <DeferredToasters />
          <BrowserRouter>
            <Suspense fallback={<RouteLoading label="Loading page…" />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<AboutUs />} />
                
                <Route path="/faqs" element={<FAQs />} />
                <Route path="/services" element={<Services />} />
                <Route path="/blogs" element={<Blogs />} />
                <Route path="/blogs/:slug" element={<BlogDetail />} />

                <Route path="/contact" element={<ContactUs />} />
                
                <Route path="/select-parking" element={<SelectParking />} />
                <Route path="/booking/profile" element={<BookingProfile />} />
                <Route path="/booking/confirm" element={<BookingConfirm />} />
                <Route path="/booking/cancel" element={<BookingCancelled />} />
                <Route path="/booking/success" element={<BookingSuccess />} />
                <Route path="/thankyou" element={<BookingSuccess />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/testimonials" element={<TestimonialsPage />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </DomainConfigProvider>
  </QueryClientProvider>
);

export default App;
